import { POI_LOOKS } from "@eldrun/content";
import { hash32 } from "@eldrun/shared";
import { withCrownNormals } from "../render/geometry/crown-normals.ts";
import { CHUNK_SIZE, WORLD_HALF, type Poi } from "@eldrun/worldgen";
import { BufferAttribute, BufferGeometry, Matrix4, Mesh, Quaternion, Vector3, type Material, type Object3D } from "three";
import { models, type ModelLibrary } from "../render/model-library.ts";
import { poiLookGeometry } from "../render/poi-meshes.ts";
import { QUALITY } from "../render/quality.ts";
import { variantOf } from "./resource-layer.ts";

/** Distâncias (m) dos POIs: a montagem completa e a silhueta; além disso, nada. */
export const POI_LOD = { near: 160, far: 400 } as const;

/**
 * Montagem de um POI, sorteada pelo id e pela seed da ilha: igual para todos os jogadores. Só o id
 * não basta: os POIs são numerados na ordem do planejador, e o Naufrágio (sempre o 0) teria a mesma
 * montagem em toda ilha.
 */
export function poiLook(poi: Pick<Poi, "id" | "type">, seed: number): number {
  return variantOf(hash32(seed, poi.id), POI_LOOKS[poi.type]?.length ?? 0);
}

export interface PoiLayerOptions {
  /** Seed da ilha (sorteio das montagens). */
  seed?: number;
  library?: ModelLibrary;
}

/** Um POI com montagem: a geometria de perto e a de longe (no espaço dele) e a matriz no mundo. */
interface Placed {
  index: number;
  near: BufferGeometry;
  far: BufferGeometry | undefined;
  matrix: Matrix4;
}

/** Uma montagem posta num anel: o POI (índice no plano), a geometria do LOD e a matriz no mundo. */
interface RingItem {
  index: number;
  geometry: BufferGeometry;
  matrix: Matrix4;
}

/**
 * Anel de distância: as montagens dos POIs no alcance, fundidas no espaço do mundo numa malha só.
 * Só é refeito quando muda o conjunto de POIs do anel.
 */
class Ring {
  readonly mesh: Mesh;
  private key = "";

  constructor(root: Object3D, material: Material, name: string) {
    this.mesh = new Mesh(new BufferGeometry(), material);
    this.mesh.name = name;
    this.mesh.visible = false;
    root.add(this.mesh);
  }

  show(items: readonly RingItem[]): void {
    const key = items.map((i) => i.index).join(",");
    if (key === this.key) return;
    this.key = key;
    this.mesh.geometry.dispose();
    this.mesh.geometry = bake(items);
    this.mesh.visible = items.length > 0;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
  }
}

/** Junta as geometrias (sem índice, F59; posição, cor e vento em float) já postas no mundo. */
function bake(items: readonly RingItem[]): BufferGeometry {
  const out = new BufferGeometry();
  if (items.length === 0) return out;
  let vertices = 0;
  for (const { geometry } of items) vertices += geometry.getAttribute("position").count;
  const position = new Float32Array(vertices * 3);
  const color = new Float32Array(vertices * 3);
  const wind = new Float32Array(vertices);
  let v = 0;
  for (const { geometry, matrix } of items) {
    const p = geometry.getAttribute("position").array;
    const m = matrix.elements;
    const n = p.length / 3;
    for (let k = 0; k < n; k++) {
      const x = p[k * 3]!;
      const y = p[k * 3 + 1]!;
      const z = p[k * 3 + 2]!;
      position[(v + k) * 3] = m[0]! * x + m[4]! * y + m[8]! * z + m[12]!;
      position[(v + k) * 3 + 1] = m[1]! * x + m[5]! * y + m[9]! * z + m[13]!;
      position[(v + k) * 3 + 2] = m[2]! * x + m[6]! * y + m[10]! * z + m[14]!;
    }
    color.set(geometry.getAttribute("color").array, v * 3);
    wind.set(geometry.getAttribute("wind").array, v);
    v += n;
  }
  out.setAttribute("position", new BufferAttribute(position, 3));
  out.setAttribute("color", new BufferAttribute(color, 3));
  out.setAttribute("wind", new BufferAttribute(wind, 1));
  out.computeBoundingSphere();
  // Fase 14: a normal de moita das copas, já no mundo.
  return withCrownNormals(out);
}

/**
 * Adereços dos pontos de interesse (doc 04; F43), desenhados a partir do plano (o mesmo do servidor,
 * sem passar pela rede). Cada POI usa uma montagem do tipo (`POI_LOOKS`, `poiLook`): as
 * peças são fundidas uma vez por tipo, montagem e LOD ao carregar o mapa. Os POIs perto da câmera
 * (até 160 m) entram com a montagem inteira e os de longe (até 400 m) só com a silhueta, as duas
 * vezes a distância de visão das Opções. Os de cada anel ficam numa malha só, no espaço do mundo,
 * refeita quando a câmera troca de célula (32 m) e o conjunto do anel muda: no máximo 2 draw calls
 * de qualquer ponto, por mais montagens diferentes que estejam à vista. Toda montagem fica no chão
 * do centro do POI, que é plano (carimbo). As estruturas interativas (baús, mercadores, Estátuas,
 * Menires, altares) vêm do schema.
 */
export class PoiLayer {
  /** Peças no mapa inteiro (todas as montagens). */
  readonly instances: number;
  private readonly placed: Placed[] = [];
  private readonly looks = new Map<string, { near: BufferGeometry; far: BufferGeometry | undefined }>();
  private readonly near: Ring;
  private readonly far: Ring;
  private lastKey = "";
  private drawnCount = 0;

  constructor(root: Object3D, material: Material, pois: readonly Poi[], height: (x: number, z: number) => number, options: PoiLayerOptions = {}) {
    const library = options.library ?? models.library;
    this.near = new Ring(root, material, "poi-perto");
    this.far = new Ring(root, material, "poi-longe");
    const q = new Quaternion();
    const up = new Vector3(0, 1, 0);
    const one = new Vector3(1, 1, 1);
    let pieces = 0;
    pois.forEach((poi, index) => {
      const looks = POI_LOOKS[poi.type];
      if (!looks?.length) return;
      const look = poiLook(poi, options.seed ?? 0);
      const key = `${poi.type}/${look}`;
      let geometry = this.looks.get(key);
      if (!geometry) {
        geometry = { near: poiLookGeometry(poi.type, look, 0, library)!, far: poiLookGeometry(poi.type, look, 1, library) };
        this.looks.set(key, geometry);
      }
      const matrix = new Matrix4().compose(new Vector3(poi.x, height(poi.x, poi.z), poi.z), q.setFromAxisAngle(up, poi.rot), one);
      this.placed.push({ index, ...geometry, matrix });
      pieces += looks[look]!.length;
    });
    this.instances = pieces;
  }

  /**
   * Refaz os anéis se a câmera mudou de célula (32 m) ou a distância de visão das Opções mudou, e o
   * conjunto de algum deles mudou.
   */
  update(cameraX: number, cameraZ: number): void {
    const key = `${Math.floor((cameraX + WORLD_HALF) / (CHUNK_SIZE / 4))},${Math.floor((cameraZ + WORLD_HALF) / (CHUNK_SIZE / 4))},${QUALITY.distance}`;
    if (key === this.lastKey) return;
    this.lastKey = key;
    const near: RingItem[] = [];
    const far: RingItem[] = [];
    const nearMax = (POI_LOD.near * QUALITY.distance) ** 2;
    const farMax = (POI_LOD.far * QUALITY.distance) ** 2;
    for (const p of this.placed) {
      const dx = p.matrix.elements[12]! - cameraX;
      const dz = p.matrix.elements[14]! - cameraZ;
      const d2 = dx * dx + dz * dz;
      if (d2 <= nearMax) near.push({ index: p.index, geometry: p.near, matrix: p.matrix });
      else if (d2 <= farMax && p.far) far.push({ index: p.index, geometry: p.far, matrix: p.matrix });
    }
    this.near.show(near);
    this.far.show(far);
    this.drawnCount = near.length + far.length;
  }

  /** POIs desenhados agora (de perto ou pela silhueta). */
  get drawn(): number {
    return this.drawnCount;
  }

  /** Malhas com algo a desenhar (cada uma é um draw call): no máximo 2. */
  get meshes(): number {
    return (this.near.mesh.visible ? 1 : 0) + (this.far.mesh.visible ? 1 : 0);
  }

  dispose(): void {
    this.near.dispose();
    this.far.dispose();
    for (const { near, far } of this.looks.values()) {
      near.dispose();
      far?.dispose();
    }
    this.looks.clear();
  }
}
